/**
 * SSO Callback Page - Handles Microsoft & Google SSO redirect
 *
 * URL params:
 *  ?success=true                                   -> cookie set, check session
 *  ?success=false&error=...                        -> show error
 *  ?needs_completion=true&provider=...&...          -> registration completion form
 */

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authApi } from "../../services/authApi";
import { captureAppEvent } from "../../lib/posthogTracking";
import { useLang } from "../../shared/i18n";
import AuthCardShell from "./AuthCardShell";
import AuthLegalNotice from "./AuthLegalNotice";
import "./AuthPages.scss";

const SSO_ERROR_KEYS: Record<string, string> = {
  b2b_account: "ssoErrB2bAccount",
  invalid_state: "ssoErrInvalidState",
  email_not_found: "ssoErrEmailNotFound",
  sso_not_configured: "ssoErrNotConfigured",
  missing_code: "ssoErrMissingCode",
};

const SsoCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { checkSession } = useAuth();
  const { t } = useLang();

  const [status, setStatus] = useState<"loading" | "completion" | "error">(
    "loading",
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [provider, setProvider] = useState<string>(
    () => params.get("provider") || "google",
  );

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [completionToken, setCompletionToken] = useState("");

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    const success = params.get("success");
    const error = params.get("error");
    const needsCompletion = params.get("needs_completion");
    const prov = params.get("provider") || "microsoft";
    setProvider(prov);

    if (success === "true") {
      const urlToken = params.get("token");
      if (urlToken) {
        localStorage.setItem("access_token", urlToken);
        window.history.replaceState(
          {},
          "",
          window.location.pathname + "?success=true",
        );
      }
      const ok = await checkSession();
      if (ok) {
        captureAppEvent("user_logged_in", { method: prov });
        navigate("/reconciliation", { replace: true });
      } else {
        captureAppEvent("user_login_failed", { reason_code: "sso_session_failed" });
        setStatus("error");
        setErrorMsg(t("ssoSessionFailed"));
      }
      return;
    }

    if (needsCompletion === "true") {
      setCompletionToken(params.get("completion_token") || "");
      setName(params.get("suggested_name") || "");
      setStatus("completion");
      return;
    }

    const errKey = error || "unknown";
    captureAppEvent("user_login_failed", { reason_code: `sso_${errKey}` });
    const translationKey = SSO_ERROR_KEYS[errKey];
    setErrorMsg(
      translationKey ? t(translationKey) : error || t("ssoErrUnknown"),
    );
    setStatus("error");
  };

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!name.trim()) {
      setFormError(t("nameRequired"));
      return;
    }
    if (password && password.length < 8) {
      setFormError(t("passwordTooShort"));
      return;
    }
    if (password && password !== confirmPassword) {
      setFormError(t("passwordsNoMatch"));
      return;
    }

    setFormLoading(true);
    const completeFn =
      provider === "google"
        ? authApi.googleCompleteRegistration
        : authApi.microsoftCompleteRegistration;
    const result = await completeFn(
      completionToken,
      name.trim(),
      password || "",
    );

    if (result.success) {
      if (result.access_token) {
        localStorage.setItem("access_token", result.access_token);
      }
      const ok = await checkSession();
      if (ok) {
        captureAppEvent("user_signed_up", { method: provider });
        navigate("/reconciliation", { replace: true });
        return;
      }
    }

    setFormError(result.message || t("ssoCompletionFailed"));
    setFormLoading(false);
  };

  const providerLabel = provider === "google" ? "Google" : "Microsoft";

  if (status === "loading") {
    return (
      <div className="auth-page" style={{ flexDirection: "column", gap: 16 }}>
        <div className="sso-spinner" />
        <p style={{ color: "#fff", fontSize: 15 }}>
          {t("ssoSigningIn").replace("{provider}", providerLabel)}
        </p>
      </div>
    );
  }

  return (
    <AuthCardShell>
      {status === "error" ? (
        <>
          <div className="auth-card__header">
            <h1>{t("ssoLoginFailed")}</h1>
            <p>{errorMsg}</p>
          </div>
          <div className="auth-form">
            <button
              className="auth-btn"
              onClick={() => navigate("/login", { replace: true })}
            >
              {t("ssoBackToLogin")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="auth-card__header">
            <h1>{t("ssoCompleteTitle")}</h1>
            <p>
              {t("ssoCompleteSubtitle").replace("{provider}", providerLabel)}
            </p>
          </div>
          <form onSubmit={handleComplete} className="auth-form">
            {formError && <div className="auth-error">{formError}</div>}
            <div className="form-group">
              <label htmlFor="name">{t("nameLabel")}</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("namePlaceholder")}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                {t("passwordLabel")}{" "}
                <span style={{ color: "#999", fontWeight: 400 }}>
                  {t("ssoPasswordOptional")}
                </span>
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("ssoPasswordHint")}
              />
            </div>
            {password && (
              <div className="form-group">
                <label htmlFor="confirmPassword">
                  {t("confirmPasswordLabel")}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("confirmPasswordPlaceholder")}
                />
              </div>
            )}
            <button type="submit" className="auth-btn" disabled={formLoading}>
              {formLoading ? t("ssoCompleteLoading") : t("ssoCompleteButton")}
            </button>
          </form>
          <AuthLegalNotice />
          <div className="auth-footer">
            <span>{t("hasAccount")} </span>
            <Link to="/login">{t("signIn")}</Link>
          </div>
        </>
      )}
    </AuthCardShell>
  );
};

export default SsoCallbackPage;
